// front/src/components/TodoDetailModal.jsx
import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { getTodoDetail, updateTodo } from '../api/todos';
import { ROLE_LABELS } from '../constants/roles';
import { Check, UserRound, X } from 'lucide-react';

function fmtValidation(d) {
  if (!d) return '';
  const dt = new Date(d);
  const p = (n) => String(n).padStart(2, '0');
  return `le ${p(dt.getDate())}/${p(dt.getMonth() + 1)} à ${p(dt.getHours())}:${p(dt.getMinutes())}`;
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = async (e) => {
    e?.stopPropagation?.();
    const txt = String(value);
    try {
      await navigator.clipboard.writeText(txt);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = txt;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* ignore */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} className="text-xs border rounded px-2 py-0.5 hover:bg-slate-50">
      {copied ? 'Copié' : 'Copier'}
    </button>
  );
}

export default function TodoDetailModal({ open, todoId, profileId, onClose, onDone, onReassign }) {
  const [todo, setTodo] = useState(null);
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !todoId) return;
    setTodo(null);
    setErr('');
    getTodoDetail(todoId, profileId)
      .then(setTodo)
      .catch((e) => setErr(e.message));
  }, [open, todoId, profileId]);

  const handleDone = async () => {
    if (!todoId) return;
    setSubmitting(true);
    try {
      await updateTodo(todoId, { statutCode: 'termine', profileId });
      onDone?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Échec de la validation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReassign = () => {
    if (!todo) return;
    onReassign?.(todo);
    onClose();
  };

  const handleDelete = async () => {
    if (!todoId) return;
    setSubmitting(true);
    try {
      await updateTodo(todoId, { statutCode: 'supprime', profileId });
      onDone?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Échec de la suppression');
    } finally {
      setSubmitting(false);
    }
  };

  const pii = todo?.pii;
  const passagePii = todo?.passage_pii;
  const piiPurgee = !!todo?.anonymized_at;
  const piiRenseignee = !!(
    pii &&
    (pii.nom || pii.prenom || pii.date_naissance || pii.sexe || pii.champ_libre)
  );
  const passagePiiRenseignee = !!(
    passagePii &&
    (passagePii.nom || passagePii.prenom || passagePii.date_naissance)
  );
  const isNouveau = todo?.statut?.code === 'nouveau';
  const isCreator = todo?.profilDemandeur?.id === profileId;

  // On affiche le bloc patient uniquement si la PII est renseignée ou purgée
  const showPatientSection = piiRenseignee || passagePiiRenseignee || piiPurgee;

  return (
    <Modal
      open={open}
      title="To Do"
      onClose={onClose}
      actions={
        <div className="flex items-center gap-2">
          {isNouveau ? (
            <>
              <button
                onClick={handleDone}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 transition"
              >
                <Check className="h-4 w-4" />
                Terminer
              </button>

              <button
                onClick={handleReassign}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition"
              >
                <UserRound className="h-4 w-4" />
                Réaffecter
              </button>

              {isCreator && (
                <button
                  onClick={handleDelete}
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 transition"
                >
                  <X className="h-4 w-4" />
                  Supprimer
                </button>
              )}
            </>
          ) : (
            <button
              onClick={onClose}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Fermer
            </button>
          )}
        </div>
      }
    >
      {err && <div className="text-sm text-red-600 mb-2">{err}</div>}
      {!todo && !err && <div className="text-sm text-slate-500">Chargement…</div>}
      {todo && (
        <div className="space-y-3 text-sm">
          <div>
            <span className="text-slate-500">Nature :</span> <b>{todo.titre}</b>
          </div>

          {todo.adress_to && (
            <div>
              <span className="text-slate-500">Adresser à :</span> {todo.adress_to}
            </div>
          )}
          {todo?.passage_pii?.passage_uid && (
            <div>
              <span className="text-slate-500">Réf patient :</span> {todo?.passage_pii?.passage_uid}
            </div>
          )}
          <div>
            <span className="text-slate-500">Statut :</span> {todo.statut?.libelle}
          </div>
          <div>
            <span className="text-slate-500">Cibles :</span>{' '}
            {(todo.cibles || []).map((c) => ROLE_LABELS[c.role] || c.role).join(', ') || '-'}
          </div>
          {/^https?:\/\//i.test(todo.doctolib_ref || '') && (
            <a
              href={todo.doctolib_ref}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline"
            >
              Dossier Doctolib
            </a>
          )}

          {/* Section Patient (masquée si pas de patient)*/}
          {showPatientSection && (
            <div className="border-t pt-2">
              <div className="font-semibold mb-1">Patient</div>
              {piiPurgee ? (
                <div className="text-slate-400 italic">
                  Données patient supprimées (au-delà de 10 jours)
                </div>
              ) : (
                <div className="space-y-0.5">
                  <span className="font-mono text-xs break-all">{todo.PrenomNomDDN} </span>
                  <CopyButton value={todo[todo.paramDefTodo?.copyIcon?.[0]]} />
                </div>
              )}
            </div>
          )}

          {todo.statut?.code === 'termine' && todo.profilValidateur?.nom && (
            <div className="border-t pt-2 text-slate-600">
              Validé par {todo.profilValidateur.nom} {todo.profilValidateur.prenom}{' '}
              {fmtValidation(todo.resolved_at)}
            </div>
          )}

          {/* Commentaire */}
          {todo.comment && (
            <div className="border-t pt-2">
              <span className="text-slate-500 block mb-0.5">Commentaire :</span>
              <div className="whitespace-pre-wrap font-medium text-slate-800 bg-slate-50 p-2 rounded border border-slate-200">
                {todo.comment}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}